import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Formik, Form, ErrorMessage } from 'formik';
import TextField from '../formik/TextField';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Button from '@material-ui/core/Button';
import * as yup from 'yup';
import { insertUserAgreement } from '../../actions/userAgreement';
import Loading from './Loading';
import Box from '@material-ui/core/Box';
import LockIcon from '@material-ui/icons/Lock';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '../formik/Checkbox';
import PDFViewer from '../common/PDFViewer';
import CustomDialog from '../common/CustomDialog';
import { v4 as uuidv4 } from 'uuid';
// import iotPdf from '../../IoT_Participation_Agreement_081921.pdf';
import userAgreement from '../../connectedHome_participation_agreement.pdf'
import { getPolicySummary, getGWPolicy } from '../../actions/policy';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  link: {
    margin: theme.spacing(1.5, 0, 0),
  },
  errorMessage: {
    fontSize: '0.75rem',
    color: '#f44336',
    marginTop: '3px',
  },
}));

const policyNumberSchema = yup.object({
  isPolicyNumKnown: yup.boolean(),
  agreedToTermsOfService: yup
    .boolean()
    .required()
    .oneOf(
      [true],
      'Please confirm you have read and agree to the user agreement.'
    ),
  policyNumber: yup
    .string()
    .required('Please enter a valid policy number')
    .matches(/^[A-Za-z0-9_-]*$/, 'Only alphanumeric characters are allowed')
    .min(9, 'Policy number must be at least 9 characters')
    .max(13, 'Policy number must not be more than 13 characters long'),
  firstName: yup
    .string()
    .required('Please provide a first name')
    .max(50, 'First name cannot exceed 50 characters'),
  lastName: yup
    .string()
    .required('Please provide a last name')
    .max(50, 'Last name cannot exceed 50 characters'),
  zipCode: yup
    .string()
    .required('Please provide a zip code')
    .matches(/^\d{5}$/, 'Enter a 5 digit zip code e.g., 12345'),
});

const PolicyAuthComponent = ({ partner, company = {}, callback }) => {
  const { Name = 'N/A' } = company;
  const [isLoading, setIsLoading] = useState(false);
  const [showUserAgreement, setShowUserAgreement] = useState(false);

  const handleSubmit = async (values) => {
    setIsLoading(true);
    let isEligible = false;

    const {
      policyNumber = '',
      firstName = '',
      lastName = '',
      zipCode = '',
    } = values;
    try {
      const policySummaryResponse = await getPolicySummary(
        policyNumber,
        firstName,
        lastName,
        zipCode
      );

      if (policySummaryResponse.status === 200) {
        const {
          policyRetrieveToken: { access_token },
          effectiveDate,
        } = policySummaryResponse.data;

        const gwPolicyResponse = await getGWPolicy(
          policyNumber,
          effectiveDate,
          access_token
        );
        if (
          gwPolicyResponse.status === 200 &&
          gwPolicyResponse.data.hasOwnProperty('retrievePolicyResponse') &&
          gwPolicyResponse.data.retrievePolicyResponse.hasOwnProperty('return')
        ) {
          const { Envelope } =
            gwPolicyResponse.data.retrievePolicyResponse.return;
          const {
            CCPolicy: { BaseState, Endorsements },
          } = Envelope;
          const isHO3Policy = Endorsements.findIndex(
            (element) => element.FormNumber === 'H3-CW-P-0001'
          );
          if (BaseState === 'IL' && isHO3Policy > -1) {
            isEligible = true;
          }
        }
      }
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      insertUserAgreement({
        PartitionKey: partner,
        RowKey: uuidv4(),
        isEligible,
        ...values,
      });
      setIsLoading(false);
      callback(isEligible);
    }
  };

  // const handleCheckboxClick = () => {
  //   console.log('handle checkbox click ...');
  // };

  const classes = useStyles();

  return (
    <>
      <Typography component='h1' variant='h4' gutterBottom>
        {`... Real quick, before we send you to ${Name}`}
      </Typography>
      <Formik
        initialValues={{
          agreedToTermsOfService: false,
          policyNumber: '',
          firstName: '',
          lastName: '',
          zipCode: '',
        }}
        validationSchema={policyNumberSchema}
        onSubmit={handleSubmit}>
        {({ values, errors, setFieldValue }) => (
          <Form className={classes.form}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name='policyNumber'
                  label='Policy Number'
                  variant='outlined'
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name='firstName'
                  label='First Name'
                  variant='outlined'
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name='lastName'
                  label='Last Name'
                  variant='outlined'
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name='zipCode'
                  label='Zip Code'
                  variant='outlined'
                  fullWidth
                />
              </Grid>
            </Grid>

            <Box my={2}>
              <Grid container spacing={2} alignItems='baseline'>
                <Grid item xs={6} align='left'>
                  <Grid container item direction='column'>
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name='agreedToTermsOfService'
                            onChange={(event) => {
                              setFieldValue(
                                'agreedToTermsOfService',
                                event.target.checked
                              );
                            }}
                            color='primary'
                          />
                        }
                        label={
                          <Typography variant='body2'>
                            I have read and agree to the{' '}
                            {
                              <Link
                                href='#'
                                onClick={(event) => {
                                  console.log('show user agreement...');
                                  event.preventDefault();
                                  setShowUserAgreement(true);
                                }}
                                variant='body2'>
                                User Agreement
                              </Link>
                            }
                          </Typography>
                        }
                      />
                    </Grid>
                    <Grid item>
                      <ErrorMessage name='agreedToTermsOfService'>
                        {(msg) => (
                          <Typography
                            className={classes.errorMessage}
                            align='left'>
                            {msg}
                          </Typography>
                        )}
                      </ErrorMessage>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>

            {isLoading ? (
              <Box my={3}>
                <Loading />
              </Box>
            ) : (
              <>
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  fullWidth
                  // className={classes.submit}
                  endIcon={<LockIcon />}>
                  SUBMIT
                </Button>
              </>
            )}
            {showUserAgreement && (
              <CustomDialog
                title='AMERICAN MODERN®
              DEVICE PROGRAM PARTICIPATION AGREEMENT'
                maxWidth='xl'
                open={showUserAgreement}
                handleClose={() => setShowUserAgreement(false)}>
                {<PDFViewer src={userAgreement} />}
              </CustomDialog>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};

export default PolicyAuthComponent;
